import { useQuery } from "@apollo/client";
import { Icon, LoadingSpinner, Tag } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CampaignDetailsModal from "../../../components/common/CampaignDetailsModal/CampaignDetailsModal";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { findCodeById } from "../../../graphql/queries/findCodeById";
import { getDateWithFormat } from "../../../utils/commons";
import { FeatureToggle, useFeatureToggle } from "../../../utils/featureToggle";

const CodeInformation = ({ searchText, setCodeGroupId }) => {
  const { t } = useTranslation();
  const [codeInformation, setCodeInformation] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);
  const { programTimezone } = useContext(GlobalConfigContext);
  const [error, setError] = useState(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const showCampaignShortTitle = useFeatureToggle(
    FeatureToggle.showCampaignShortTitle
  );

  const { refetch: refetchCodeGroup, loading } = useQuery(findCodeById, {
    variables: { code: searchText },
    onCompleted: (codeGroupData) => {
      setError(null);
      setCodeInformation(codeGroupData?.findCodeById);
      setCodeGroupId(codeGroupData?.findCodeById?.codeGroup?.codeGroupId);
    },
    onError: (err) => {
      setCodeGroupId("");
      setError(`${err?.graphQLErrors?.[0]?.message || err?.message}`);
      console.error("Error fetching code group data:", err);
    },
  });

  useEffect(() => {
    setCodeGroupId("");
    refetchCodeGroup();
    setIsExpanded(false);
  }, [searchText, refetchCodeGroup, setCodeGroupId]);

  const toggleExpand = () => {
    setIsExpanded((prevState) => !prevState);
  };

  const handleCampaignClick = (campaignId) => {
    setSelectedCampaignId(campaignId);
    setShowCampaignModal(true);
  };

  const renderCodeInfoRow = () => {
    if (error) {
      return (
        <tr>
          <td colSpan={9}>{error}</td>
        </tr>
      );
    }

    if (!codeInformation?.status) {
      return (
        <tr>
          <td colSpan={9}>{t("CustomerSupport:NoResultsFoundLabel")}</td>
        </tr>
      );
    }

    return (
      <>
        <CampaignDetailsModal
          showModal={showCampaignModal}
          onClose={() => setShowCampaignModal(false)}
          campaignId={selectedCampaignId}
        />
        <tr onClick={toggleExpand}>
          <td>
            <Icon
              icon={isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
              title={isExpanded ? "Expand less" : "Expand more"}
              size="medium"
            />
          </td>
          <td>
            <Tag
              color={
                codeInformation?.status === "CLAIMED" ? "Green-150" : "Cyan-500"
              }
              text={
                codeInformation?.status === "CLAIMED"
                  ? t("CustomerSupport:UsedLabel")
                  : t("CustomerSupport:UnusedLabel")
              }
              isCopyable={false}
            />
          </td>
          <td>{codeInformation?.codeGroup?.basicSetup?.codeGroupName}</td>
          <td>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleCampaignClick(codeInformation?.campaign?.id);
              }}
            >
              {codeInformation?.campaign?.title || "-"}
            </a>
          </td>
          <td>
            {showCampaignShortTitle
              ? codeInformation?.campaign?.titleShort || "-"
              : "-"}
          </td>
          <td>{codeInformation?.profile?.id || "-"}</td>
          <td>
            {codeInformation?.profile?.userInfo ? (
              <span>
                {codeInformation?.profile?.userInfo?.firstName}{" "}
                {codeInformation?.profile?.userInfo?.middleName}{" "}
                {codeInformation?.profile?.userInfo?.lastName}
              </span>
            ) : (
              <span>-</span>
            )}
          </td>
          <td>{codeInformation?.profile?.userInfo?.emailAddress || "-"}</td>
          <td>{codeInformation?.profile?.userInfo?.nationalId || "-"}</td>
        </tr>
      </>
    );
  };

  const renderRewardsTable = () => {
    if (!isExpanded) {
      return null;
    }
    if (!codeInformation?.rewards?.length > 0) {
      return (
        <tr>
          <td colSpan="8" className="text-center">
            {t("CustomerSupport:NoRewardsFoundLabel")}
          </td>
        </tr>
      );
    }
    return (
      <tr>
        <td colSpan="8" style={{ padding: 0 }}>
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>{t("CustomerSupport:RewardName")}</th>
                <th>{t("CustomerSupport:AmountUnitColumn")}</th>
                <th>{t("CustomerSupport:QuantityColumn")}</th>
                <th>{t("CustomerSupport:WinDateColumn")}</th>
                <th>{t("CustomerSupport:ExpireDateColumn")}</th>
              </tr>
            </thead>
            <tbody>
              {codeInformation?.rewards?.map((reward) => (
                <tr key={reward?.id}>
                  <td>{reward?.name}</td>
                  <td>
                    {reward?.quantity} / {reward?.unitName}
                  </td>
                  <td>{reward?.rewardGroupRewardQuantity}</td>
                  <td>
                    {reward?.createdDate
                      ? getDateWithFormat(reward?.createdDate, programTimezone)
                      : "-"}
                  </td>
                  <td>
                    {reward?.walletWithdrawalDate
                      ? getDateWithFormat(
                          reward?.walletWithdrawalDate,
                          programTimezone
                        )
                      : codeInformation?.campaign?.activeEndDate
                        ? getDateWithFormat(
                            codeInformation?.campaign?.activeEndDate,
                            programTimezone
                          )
                        : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    );
  };

  return (
    <div className="list-container">
      {loading ? (
        <LoadingSpinner
          size={{ height: "30px", width: "30px" }}
          style={{ margin: "auto" }}
        />
      ) : (
        <table className="list-table">
          <thead>
            <tr>
              <th> </th>
              <th>{t("CustomerSupport:StatusColumn")}</th>
              <th>{t("CustomerSupport:CodeGroupColumn")}</th>
              <th>{t("CustomerSupport:CampaignTitleColumn")}</th>
              <th>{t("CustomerSupport:CampaignTitleShortColumn")}</th>
              <th>{t("CustomerSupport:ProfileIDLabel")}</th>
              <th>{t("CustomerSupport:NameColumn")}</th>
              <th>{t("CustomerSupport:EmailAddressLabel")}</th>
              <th>{t("CustomerSupport:NationalIDLabel")}</th>
            </tr>
          </thead>
          <tbody>
            {renderCodeInfoRow()}
            {renderRewardsTable()}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CodeInformation;
